import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AtSign,
  CheckCircle2,
  Image as ImageIcon,
  Landmark,
  Loader2,
  Plug,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { useCompaniesApi, useRegistryApi, useKsefApi } from '@/services/api';
import { validateIban, formatIban, cleanIban } from '@/utils/iban';
import type {
  CompanyBankAccount,
  CompanyRepresentative,
  KsefConfig,
  LogoInk,
  RepresentationMode,
} from '@ip/shared-types';
import { CompanyLogo } from './CompanyLogo';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/shared/DatePicker';

interface CompanyEditModalProps {
  open: boolean;
  companyNip: string;
  companyName: string;
  onClose: () => void;
  onSaved?: () => void;
}

const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP', 'CHF', 'CZK', 'SEK', 'NOK', 'DKK'];

// Common board/proxy titles offered as datalist suggestions — the field
// stays a free string (any function name is valid on a contract).
const REPRESENTATIVE_TITLES = ['Prezes Zarządu', 'Członek Zarządu', 'Prokurent'];

interface CompanyFormValues {
  name: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  bdoNumber: string;
  krsNumber: string;
  contactEmail: string;
  website: string;
}

const EMPTY_FORM: CompanyFormValues = {
  name: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'PL',
  bdoNumber: '',
  krsNumber: '',
  contactEmail: '',
  website: '',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      {children}
    </div>
  );
}

export function CompanyEditModal({
  open,
  companyNip,
  companyName,
  onClose,
  onSaved,
}: CompanyEditModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingBir, setFetchingBir] = useState(false);
  const [values, setValues] = useState<CompanyFormValues>(EMPTY_FORM);
  const [bankAccounts, setBankAccounts] = useState<CompanyBankAccount[]>([]);
  const [representationMode, setRepresentationMode] = useState<RepresentationMode | null>(null);
  const [representatives, setRepresentatives] = useState<CompanyRepresentative[]>([]);
  const [emailDomains, setEmailDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [ksefConfig, setKsefConfig] = useState<KsefConfig | null>(null);
  const [ksefLoading, setKsefLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [certPassword, setCertPassword] = useState('');
  const [certFile, setCertFile] = useState<File | null>(null);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const certInputRef = useRef<HTMLInputElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);

  // Logo: uploaded/removed immediately via its own endpoint (not part of Save),
  // because it is a file, not a form field.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoInk, setLogoInk] = useState<LogoInk | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoDragging, setLogoDragging] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const { getCompanyDetail, updateCompany, uploadCompanyLogo, deleteCompanyLogo } =
    useCompaniesApi();
  const { lookupRegistry } = useRegistryApi();
  const {
    getConfig: getKsefConfig,
    updateConfig: updateKsefConfig,
    uploadCertificate,
    removeCertificate,
    testConnection,
    triggerSync,
  } = useKsefApi();

  const setField = <K extends keyof CompanyFormValues>(field: K, value: CompanyFormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const loadCompany = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanyDetail(companyNip);
      setBankAccounts(data.bankAccounts || []);
      setRepresentationMode(data.representationMode ?? null);
      setRepresentatives(data.representatives || []);
      setEmailDomains(data.emailDomains || []);
      setDomainInput('');
      setLogoUrl(data.logoUrl ?? null);
      setLogoInk(data.logoInk ?? null);
      setLogoError(null);
      setValues({
        name: data.name || '',
        street: data.street || '',
        postalCode: data.postalCode || '',
        city: data.city || '',
        country: data.country || 'PL',
        bdoNumber: data.bdoNumber || '',
        krsNumber: data.krsNumber || '',
        contactEmail: data.contactEmail || '',
        website: data.website || '',
      });
      // Load KSeF config (don't fail if not available)
      try {
        const ksefData = await getKsefConfig(companyNip);
        setKsefConfig(ksefData);
      } catch {
        setKsefConfig(null);
      }
    } catch (error) {
      toast.error(t('companies.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, getCompanyDetail, getKsefConfig]);

  useEffect(() => {
    if (open) {
      loadCompany();
    }
  }, [open, loadCompany]);

  // Mirrors the backend's bare-domain rule (lowercase labels, no '@'/scheme).
  const DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

  /** Turns the pending input into a chip; returns the effective list (null = invalid input). */
  const commitDomainInput = (): string[] | null => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) return emailDomains;
    if (!DOMAIN_RE.test(domain)) {
      toast.error(t('companies.emailDomains.invalid', { domain }));
      return null;
    }
    const next = emailDomains.includes(domain) ? emailDomains : [...emailDomains, domain];
    setEmailDomains(next);
    setDomainInput('');
    return next;
  };

  const removeDomain = (domain: string) => {
    setEmailDomains((prev) => prev.filter((d) => d !== domain));
  };

  // ===== Logo =====

  // Client-side pre-checks mirror the backend rules (which re-validates the
  // actual bytes) so the common mistakes get an instant inline error.
  const LOGO_MAX_BYTES = 2 * 1024 * 1024;
  const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

  const handleLogoFile = async (file: File) => {
    setLogoError(null);
    if (!LOGO_TYPES.includes(file.type)) {
      setLogoError(t('companies.logo.wrongType'));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(t('companies.logo.tooLarge'));
      return;
    }
    try {
      setLogoBusy(true);
      const { logoUrl: url, logoInk: ink } = await uploadCompanyLogo(companyNip, file);
      setLogoUrl(url);
      setLogoInk(ink);
      toast.success(t('companies.logo.uploaded'));
      onSaved?.(); // refresh the sidebar switcher
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : t('companies.logo.uploadFailed');
      setLogoError(message);
    } finally {
      setLogoBusy(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = async () => {
    setLogoError(null);
    try {
      setLogoBusy(true);
      await deleteCompanyLogo(companyNip);
      setLogoUrl(null);
      setLogoInk(null);
      toast.success(t('companies.logo.removed'));
      onSaved?.();
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : t('companies.logo.removeFailed');
      setLogoError(message);
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSave = async () => {
    for (let i = 0; i < bankAccounts.length; i++) {
      const account = bankAccounts[i];
      if (!account.iban) {
        toast.error(t('companies.bankAccount.ibanRequired', { index: i + 1 }));
        return;
      }
      const ibanResult = validateIban(account.iban);
      if (!ibanResult.valid) {
        toast.error(t('companies.bankAccount.ibanInvalid', { index: i + 1, error: ibanResult.error ?? '' }));
        return;
      }
      if (!account.currency) {
        toast.error(t('companies.bankAccount.currencyRequired', { index: i + 1 }));
        return;
      }
    }

    // Mirrors the backend validation (representation.util.ts) so mistakes get
    // a friendly toast instead of a raw 400.
    for (let i = 0; i < representatives.length; i++) {
      const rep = representatives[i];
      if (!rep.fullName.trim()) {
        toast.error(t('companies.representation.fullNameRequired', { index: i + 1 }));
        return;
      }
      if (!rep.title.trim()) {
        toast.error(t('companies.representation.titleRequired', { index: i + 1 }));
        return;
      }
    }
    if (representationMode === 'two_person' && representatives.length < 2) {
      toast.error(t('companies.representation.twoPersonMin'));
      return;
    }

    // A domain typed but not yet chip-committed still counts — nobody should
    // lose it just because they clicked Save instead of pressing Enter.
    const finalDomains = commitDomainInput();
    if (finalDomains === null) return;

    try {
      setSaving(true);

      const cleanedAccounts = bankAccounts.map((account) => ({
        ...account,
        iban: cleanIban(account.iban),
      }));

      await updateCompany(companyNip, {
        name: values.name,
        street: values.street,
        postalCode: values.postalCode,
        city: values.city,
        country: values.country,
        bdoNumber: values.bdoNumber.trim() || null,
        krsNumber: values.krsNumber.trim() || null,
        contactEmail: values.contactEmail.trim() || null,
        website: values.website.trim() || null,
        bankAccounts: cleanedAccounts,
        emailDomains: finalDomains,
        representationMode,
        // List order IS signing order — the backend derives sortOrder from it.
        representatives: representatives.map((rep) => ({
          fullName: rep.fullName.trim(),
          title: rep.title.trim(),
          userId: rep.userId ?? null,
        })),
      });

      toast.success(t('companies.saved'));
      onSaved?.();
      onClose();
    } catch (error) {
      // Surface the server message — the 409 for a domain already owned by
      // another company tells the user exactly which domain and company.
      toast.error(error instanceof Error && error.message ? error.message : t('companies.saveFailed'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleFetchFromBir = async () => {
    try {
      setFetchingBir(true);
      const data = await lookupRegistry(companyNip.replace(/[\s-]/g, ''));

      // Fill only what the registry actually returned — never clobber
      // manually entered fields with empty values.
      setValues((prev) => ({
        ...prev,
        name: data.name || prev.name,
        street: data.street || prev.street,
        postalCode: data.postalCode || prev.postalCode,
        city: data.city || prev.city,
        country: data.country || prev.country || 'PL',
        krsNumber: data.krs || prev.krsNumber,
      }));
      toast.success(t('registry.lookupSuccess'));
    } catch (error) {
      toast.error(t('registry.lookupFailed'));
      console.error(error);
    } finally {
      setFetchingBir(false);
    }
  };

  const addBankAccount = () => {
    setBankAccounts([...bankAccounts, { iban: '', currency: 'PLN', name: '' }]);
  };

  const removeBankAccount = (index: number) => {
    setBankAccounts(bankAccounts.filter((_, i) => i !== index));
  };

  const updateBankAccount = (
    index: number,
    field: keyof CompanyBankAccount,
    value: string,
  ) => {
    const updated = [...bankAccounts];
    updated[index] = { ...updated[index], [field]: value };
    setBankAccounts(updated);
  };

  const addRepresentative = () => {
    setRepresentatives([...representatives, { fullName: '', title: '' }]);
  };

  const removeRepresentative = (index: number) => {
    setRepresentatives(representatives.filter((_, i) => i !== index));
  };

  const updateRepresentative = (index: number, field: 'fullName' | 'title', value: string) => {
    const updated = [...representatives];
    updated[index] = { ...updated[index], [field]: value };
    setRepresentatives(updated);
  };

  const validateAccountIban = (index: number): string | null => {
    const account = bankAccounts[index];
    if (!account.iban) return null;
    const result = validateIban(account.iban);
    return result.valid ? null : result.error || 'Invalid IBAN';
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Company: {companyName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="text-muted-foreground size-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Company Information */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="text-sm font-medium">Company Information</CardTitle>
                <CardAction>
                  <Button variant="outline" size="sm" onClick={handleFetchFromBir} disabled={fetchingBir}>
                    {fetchingBir && <Loader2 className="size-4 animate-spin" />}
                    {t('registry.fetch')}
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="space-y-3 px-4">
                <Field label="NIP">
                  <Input value={companyNip} disabled />
                </Field>
                <Field label="Name">
                  <Input
                    placeholder="Company name"
                    value={values.name}
                    onChange={(e) => setField('name', e.target.value)}
                  />
                </Field>
                <Field label="Street">
                  <Input
                    placeholder="Street address"
                    value={values.street}
                    onChange={(e) => setField('street', e.target.value)}
                  />
                </Field>
                <div className="grid grid-cols-[1fr_1.25fr_0.75fr] gap-2">
                  <Field label="Postal Code">
                    <Input
                      placeholder="00-000"
                      value={values.postalCode}
                      onChange={(e) => setField('postalCode', e.target.value)}
                    />
                  </Field>
                  <Field label="City">
                    <Input
                      placeholder="City"
                      value={values.city}
                      onChange={(e) => setField('city', e.target.value)}
                    />
                  </Field>
                  <Field label="Country">
                    <Input
                      placeholder="PL"
                      value={values.country}
                      onChange={(e) => setField('country', e.target.value)}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t('companies.form.bdoNumber')}>
                    <Input
                      value={values.bdoNumber}
                      onChange={(e) => setField('bdoNumber', e.target.value)}
                    />
                  </Field>
                  <Field label={t('companies.form.krsNumber')}>
                    <Input
                      value={values.krsNumber}
                      onChange={(e) => setField('krsNumber', e.target.value)}
                    />
                  </Field>
                  <Field label={t('companies.form.contactEmail')}>
                    <Input
                      type="email"
                      value={values.contactEmail}
                      onChange={(e) => setField('contactEmail', e.target.value)}
                    />
                  </Field>
                  <Field label={t('companies.form.website')}>
                    <Input
                      type="url"
                      placeholder="https://…"
                      value={values.website}
                      onChange={(e) => setField('website', e.target.value)}
                    />
                  </Field>
                </div>
              </CardContent>
            </Card>

            {/* Company logo */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <ImageIcon className="size-4" />
                  {t('companies.logo.label')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 px-4">
                <p className="text-muted-foreground text-xs">{t('companies.logo.hint')}</p>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={t('companies.logo.choose')}
                  className={`flex items-center gap-4 rounded-md border border-dashed p-3 transition-colors ${
                    logoDragging ? 'border-primary bg-primary/5' : 'border-input'
                  } ${logoBusy ? 'pointer-events-none opacity-60' : 'cursor-pointer hover:bg-accent/40'}`}
                  onClick={() => logoInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      logoInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setLogoDragging(true);
                  }}
                  onDragLeave={() => setLogoDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setLogoDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) void handleLogoFile(file);
                  }}
                >
                  {/* Same plate logic as the sidebar (see CompanyLogo): the
                      preview must show exactly what the sidebar will show. */}
                  {logoBusy ? (
                    <div className="bg-muted flex size-16 shrink-0 items-center justify-center rounded-lg border">
                      <Loader2 className="text-muted-foreground size-5 animate-spin" />
                    </div>
                  ) : logoUrl ? (
                    <CompanyLogo
                      src={logoUrl}
                      alt={t('companies.logo.alt', { company: companyName })}
                      ink={logoInk}
                      className="size-16"
                    />
                  ) : (
                    <div className="bg-muted flex size-16 shrink-0 items-center justify-center rounded-lg border">
                      <ImageIcon className="text-muted-foreground size-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm">
                      {logoUrl ? t('companies.logo.replace') : t('companies.logo.dropzone')}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t('companies.logo.constraints')}
                    </p>
                  </div>
                  {logoUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logoBusy}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleLogoRemove();
                      }}
                    >
                      <Trash2 className="size-4" />
                      {t('companies.logo.remove')}
                    </Button>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleLogoFile(file);
                  }}
                />
                {logoError && (
                  <p className="text-destructive text-xs" role="alert">
                    {logoError}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Employee e-mail domains (HR auto-provisioning) */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <AtSign className="size-4" />
                  {t('companies.emailDomains.label')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 px-4">
                <p className="text-muted-foreground text-xs">{t('companies.emailDomains.hint')}</p>
                {emailDomains.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {emailDomains.map((domain) => (
                      <Badge key={domain} variant="secondary" className="gap-1 pr-1 font-mono">
                        {domain}
                        <button
                          type="button"
                          aria-label={`Remove ${domain}`}
                          className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
                          onClick={() => removeDomain(domain)}
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <Input
                  value={domainInput}
                  placeholder={t('companies.emailDomains.placeholder')}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      commitDomainInput();
                    }
                  }}
                  onBlur={() => {
                    if (domainInput.trim()) commitDomainInput();
                  }}
                />
              </CardContent>
            </Card>

            {/* Bank Accounts */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Landmark className="size-4" />
                  Bank Accounts
                </CardTitle>
                <CardAction>
                  <Button variant="outline" size="sm" onClick={addBankAccount}>
                    <Plus className="size-4" />
                    Add Account
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="space-y-3 px-4">
                {bankAccounts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No bank accounts configured</p>
                ) : (
                  bankAccounts.map((account, index) => {
                    const ibanError = validateAccountIban(index);
                    return (
                      <div key={index} className="space-y-2 rounded-md border p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Account {index + 1}</span>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => removeBankAccount(index)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="col-span-2">
                            <Field label="IBAN">
                              <Input
                                value={formatIban(account.iban)}
                                onChange={(e) => updateBankAccount(index, 'iban', e.target.value)}
                                placeholder="PL00 0000 0000 0000 0000 0000 0000"
                                aria-invalid={ibanError ? true : undefined}
                              />
                            </Field>
                            {ibanError && (
                              <p className="text-destructive mt-1 text-xs">{ibanError}</p>
                            )}
                          </div>
                          <Field label="Currency">
                            <Select
                              value={account.currency}
                              onValueChange={(value) => updateBankAccount(index, 'currency', value)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CURRENCIES.map((c) => (
                                  <SelectItem key={c} value={c}>
                                    {c}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Field label="Account Name (optional)">
                            <Input
                              value={account.name || ''}
                              onChange={(e) => updateBankAccount(index, 'name', e.target.value)}
                              placeholder="e.g., Main account, EUR account"
                            />
                          </Field>
                          <Field label="SWIFT/BIC (optional)">
                            <Input
                              value={account.swiftBic || ''}
                              onChange={(e) =>
                                updateBankAccount(index, 'swiftBic', e.target.value.toUpperCase())
                              }
                              placeholder="e.g., BREXPLPWXXX"
                              maxLength={11}
                            />
                          </Field>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            {/* Company representation (contract signing) */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Users className="size-4" />
                  {t('companies.representation.label')}
                </CardTitle>
                <CardAction>
                  <Button variant="outline" size="sm" onClick={addRepresentative}>
                    <Plus className="size-4" />
                    {t('companies.representation.add')}
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="space-y-3 px-4">
                <p className="text-muted-foreground text-xs">
                  {t('companies.representation.hint')}
                </p>
                <Field label={t('companies.representation.mode')}>
                  <Select
                    value={representationMode ?? 'none'}
                    onValueChange={(value) =>
                      setRepresentationMode(
                        value === 'none' ? null : (value as RepresentationMode),
                      )
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {t('companies.representation.modeNotSet')}
                      </SelectItem>
                      <SelectItem value="one_person">
                        {t('companies.representation.modeOnePerson')}
                      </SelectItem>
                      <SelectItem value="two_person">
                        {t('companies.representation.modeTwoPerson')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {representatives.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    {t('companies.representation.empty')}
                  </p>
                ) : (
                  representatives.map((rep, index) => (
                    <div key={index} className="space-y-2 rounded-md border p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t('companies.representation.person', { index: index + 1 })}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removeRepresentative(index)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label={t('companies.representation.fullName')}>
                          <Input
                            value={rep.fullName}
                            placeholder={t('companies.representation.fullNamePlaceholder')}
                            onChange={(e) =>
                              updateRepresentative(index, 'fullName', e.target.value)
                            }
                          />
                        </Field>
                        <Field label={t('companies.representation.titleLabel')}>
                          <Input
                            value={rep.title}
                            list="company-representative-titles"
                            placeholder={t('companies.representation.titlePlaceholder')}
                            onChange={(e) => updateRepresentative(index, 'title', e.target.value)}
                          />
                        </Field>
                      </div>
                    </div>
                  ))
                )}
                <datalist id="company-representative-titles">
                  {REPRESENTATIVE_TITLES.map((title) => (
                    <option key={title} value={title} />
                  ))}
                </datalist>
              </CardContent>
            </Card>

            {/* KSeF Integration */}
            <Card className="gap-3 py-4">
              <CardHeader className="px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <ShieldCheck className="size-4" />
                  {t('ksef.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('ksef.enabled')}>
                    <div>
                      <Switch
                        checked={ksefConfig?.enabled ?? false}
                        disabled={ksefLoading}
                        onCheckedChange={async (checked) => {
                          setKsefLoading(true);
                          try {
                            await updateKsefConfig(companyNip, { enabled: checked });
                            setKsefConfig((prev) => (prev ? { ...prev, enabled: checked } : null));
                            toast.success(checked ? t('ksef.enabledToast') : t('ksef.disabledToast'));
                          } catch {
                            toast.error(t('ksef.configUpdateFailed'));
                          } finally {
                            setKsefLoading(false);
                          }
                        }}
                      />
                    </div>
                  </Field>
                  <Field label={t('ksef.environment')}>
                    <Select
                      value={ksefConfig?.environment ?? 'test'}
                      onValueChange={async (value) => {
                        const environment = value as 'test' | 'production';
                        setKsefLoading(true);
                        try {
                          await updateKsefConfig(companyNip, { environment });
                          setKsefConfig((prev) => (prev ? { ...prev, environment } : null));
                        } catch {
                          toast.error(t('ksef.environmentUpdateFailed'));
                        } finally {
                          setKsefLoading(false);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">{t('ksef.environmentTest')}</SelectItem>
                        <SelectItem value="production">{t('ksef.environmentProduction')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label={t('ksef.syncFromDate')}>
                  <DatePicker
                    value={ksefConfig?.syncFromDate ? new Date(ksefConfig.syncFromDate) : null}
                    onChange={async (date) => {
                      const syncFromDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
                      try {
                        await updateKsefConfig(companyNip, { syncFromDate: syncFromDate ?? undefined });
                        setKsefConfig((prev) => (prev ? { ...prev, syncFromDate } : null));
                      } catch {
                        toast.error(t('ksef.syncDateUpdateFailed'));
                      }
                    }}
                  />
                </Field>

                {/* Certificate */}
                <Field label={t('ksef.certificate')}>
                  {ksefConfig?.hasCertificate ? (
                    <div className="space-y-2">
                      <Badge
                        variant="outline"
                        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                      >
                        <CheckCircle2 className="size-3" />
                        {t('ksef.certificateUploaded')}
                      </Badge>
                      {ksefConfig.certificateFingerprint && (
                        <p className="text-muted-foreground text-[11px]">
                          {t('ksef.certificateFingerprint')}:{' '}
                          {ksefConfig.certificateFingerprint.substring(0, 16)}...
                        </p>
                      )}
                      <div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={async () => {
                            try {
                              await removeCertificate(companyNip);
                              setKsefConfig((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      hasCertificate: false,
                                      certificateFingerprint: null,
                                      certificateExpiry: null,
                                    }
                                  : null,
                              );
                              toast.success(t('ksef.certificateRemoved'));
                            } catch {
                              toast.error(t('ksef.certificateRemoveFailed'));
                            }
                          }}
                        >
                          {t('ksef.removeCertificate')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Badge variant="secondary">
                        <XCircle className="size-3" />
                        {t('ksef.certificateNotUploaded')}
                      </Badge>
                      <Input
                        type="password"
                        placeholder={t('ksef.certificatePassword')}
                        value={certPassword}
                        onChange={(e) => setCertPassword(e.target.value)}
                        className="max-w-[260px]"
                      />
                      <div className="space-y-1">
                        <p className="text-muted-foreground text-xs">{t('ksef.certificateFile')}</p>
                        <input
                          ref={certInputRef}
                          type="file"
                          accept=".crt,.pem,.p12,.pfx"
                          className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                          onChange={(e) => {
                            setCertFile(e.target.files?.[0] ?? null);
                            setKeyFile(null);
                            if (keyInputRef.current) keyInputRef.current.value = '';
                          }}
                        />
                      </div>
                      {certFile && /\.(crt|pem)$/i.test(certFile.name) && (
                        <div className="space-y-1">
                          <p className="text-muted-foreground text-xs">{t('ksef.privateKeyFile')}</p>
                          <input
                            ref={keyInputRef}
                            type="file"
                            accept=".key,.pem"
                            className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                            onChange={(e) => setKeyFile(e.target.files?.[0] ?? null)}
                          />
                        </div>
                      )}
                      <div>
                        <Button
                          size="sm"
                          disabled={!certFile || uploading}
                          onClick={async () => {
                            if (!certFile) return;
                            setUploading(true);
                            try {
                              const result = await uploadCertificate(
                                companyNip,
                                certFile,
                                certPassword || undefined,
                                keyFile || undefined,
                              );
                              setKsefConfig((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      hasCertificate: true,
                                      certificateFingerprint: result.fingerprint,
                                    }
                                  : null,
                              );
                              setCertPassword('');
                              setCertFile(null);
                              setKeyFile(null);
                              if (certInputRef.current) certInputRef.current.value = '';
                              if (keyInputRef.current) keyInputRef.current.value = '';
                              toast.success(t('ksef.certificateUploaded'));
                            } catch (err) {
                              toast.error(
                                err instanceof Error ? err.message : t('ksef.certificateUploadFailed'),
                              );
                            } finally {
                              setUploading(false);
                            }
                          }}
                        >
                          {uploading ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <ShieldCheck className="size-4" />
                          )}
                          {t('ksef.uploadCertificate')}
                        </Button>
                      </div>
                    </div>
                  )}
                </Field>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={testingConnection}
                    onClick={async () => {
                      setTestingConnection(true);
                      try {
                        const result = await testConnection(companyNip);
                        if (result.success) {
                          toast.success(t('ksef.testConnectionSuccess'));
                        } else {
                          toast.error(`${t('ksef.testConnectionFailed')}: ${result.errors.join(', ')}`);
                        }
                      } catch {
                        toast.error(t('ksef.testConnectionFailed'));
                      } finally {
                        setTestingConnection(false);
                      }
                    }}
                  >
                    {testingConnection ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plug className="size-4" />
                    )}
                    {t('ksef.testConnection')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={syncing || !ksefConfig?.enabled || !ksefConfig?.hasCertificate}
                    onClick={async () => {
                      setSyncing(true);
                      try {
                        const result = await triggerSync(companyNip);
                        if (result.success) {
                          toast.success(
                            t('ksef.syncSuccess', {
                              imported: result.invoicesImported,
                              skipped: result.invoicesSkipped,
                            }),
                          );
                        } else {
                          toast.warning(
                            t('ksef.syncCompletedWithErrors', { errors: result.errors.join(', ') }),
                          );
                        }
                        // Refresh config to show updated sync status
                        const updated = await getKsefConfig(companyNip);
                        setKsefConfig(updated);
                      } catch {
                        toast.error(t('ksef.syncError'));
                      } finally {
                        setSyncing(false);
                      }
                    }}
                  >
                    <RefreshCw className={syncing ? 'size-4 animate-spin' : 'size-4'} />
                    {t('ksef.syncNow')}
                  </Button>
                </div>

                {/* Sync Status */}
                {ksefConfig?.lastSyncAt && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-xs">
                      {t('ksef.lastSync')}: {new Date(ksefConfig.lastSyncAt).toLocaleString('pl-PL')}{' '}
                      &middot;{' '}
                      {ksefConfig.lastSyncStatus === 'success' ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {t('ksef.syncStatusSuccess')}
                        </span>
                      ) : ksefConfig.lastSyncStatus === 'error' ? (
                        <span className="text-destructive">{t('ksef.syncStatusError')}</span>
                      ) : (
                        <span className="text-foreground">{ksefConfig.lastSyncStatus}</span>
                      )}
                    </p>
                    {ksefConfig.lastSyncError && (
                      <Alert variant="destructive">
                        <XCircle className="size-4" />
                        <AlertDescription>{ksefConfig.lastSyncError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
